import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BigButton } from '../../components/BigButton';
import { GenericTextInput } from '../../components/Inputs/styles';
import animations from '../../helpers/animations';
import { getErrorMessage } from '../../helpers/errorMessages';
import inputMasker from '../../helpers/inputMasker';
import authenticationService from '../../services/authentication.service';
import ForeignerContent from './ForeignerContent';
import {
    AuthModalContentProps,
    resetPasswordPageSchema,
    resetPasswordPageSchemaEn,
} from './logic';

type Fields = z.infer<typeof resetPasswordPageSchema>;

export default function ResetPasswordContent({
    setCurrentColumn,
}: AuthModalContentProps) {
    const { t, i18n } = useTranslation(undefined, {
        keyPrefix: 'reset_password',
    });

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setError,
    } = useForm<Fields>({
        resolver: zodResolver(
            i18n.language === 'pt'
                ? resetPasswordPageSchema
                : resetPasswordPageSchemaEn
        ),
        mode: 'onBlur',
    });

    const [isLoading, setIsLoading] = useState(false);

    const handleResetPassword = async (data: Fields) => {
        setIsLoading(true);

        try {
            const result =
                await authenticationService.sendRecoverPasswordRequest({
                    login: data.login,
                });

            if (result.error) {
                setError('root', { message: getErrorMessage(result.error) });
                return;
            }

            if (result.success) {
                setCurrentColumn('email-submitted');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.form
            onSubmit={handleSubmit(handleResetPassword)}
            {...animations.fadeInOut(0.05)}
        >
            <div className="title-wrapper">
                <h2 className="title">{t('title_wrapper.title')}</h2>
                <p className="description">{t('title_wrapper.description')}</p>
                <ForeignerContent />
            </div>
            <div className="text-inputs-wrapper">
                <div className="input-item">
                    <label htmlFor="cpf">
                        {t('text_inputs_wrapper.input_item.cpf_label')}
                    </label>
                    <GenericTextInput
                        id="login"
                        type="text"
                        placeholder={t(
                            'text_inputs_wrapper.input_item.cpf_placeholder'
                        )}
                        {...register('login', {
                            onChange: inputMasker.handleChange('CPFCNPJ'),
                        })}
                        maxLength={inputMasker.getMaxLength('CPFCNPJ')}
                        aria-invalid={Boolean(errors.login)}
                    />
                    {errors.login && <span>{errors.login.message}</span>}
                </div>
            </div>
            <div className="error-column">
                {errors.root && (
                    <p className="api-error">{errors.root.message!}</p>
                )}
                <BigButton
                    bgColor={isValid ? 'primary' : 'secondary'}
                    color={isValid ? 'primary' : 'secondary'}
                    type="submit"
                    cursorBlocked={!isValid}
                    isLoading={isLoading}
                >
                    {t('big_button.submit_text')}
                </BigButton>
            </div>
            <p className="create-account-notice">
                {t('create_account_notice.text')}{' '}
                <a onClick={() => setCurrentColumn('login')}>
                    {t('create_account_notice.link_text')}
                </a>
            </p>
        </motion.form>
    );
}
